import React, { useEffect, useState, lazy, Suspense } from 'react';
import BottomNavBar from './components/BottomNavBar';
import MyBasket from './components/MyBasket';
import Login from './components/Login';
import SideDrawer from './components/SideDrawer';
import CheckInRequiredModal from './components/CheckInRequiredModal';
import GlobalSearch from './components/GlobalSearch';
import { SearchProvider } from './contexts/SearchContext';
import { LoadingProvider, useLoading } from './contexts/LoadingContext';
import { ToastProvider } from './contexts/ToastContext';
import { MenuIcon } from 'lucide-react';
import MainContentLoader from './components/common/MainContentLoader';
import NotificationBell from './components/NotificationBell';
import { getDeliveryProfile } from './services/profile.service';
import {
  readPartnerCheckedInFromProfileStorage,
  subscribeMidnightAttendanceReset,
} from './utils/partnerAttendanceUi';
import { getDeliveryManagerPhone, getEmergencyContactDisplay } from './utils/deliveryProfileDisplay';
import { clearSession, scheduleAccessTokenRefresh } from './utils/apiConfig';
import { logoutDeliveryPartner } from './services/auth.Service';
import {
  startDeliverySession,
  getDeliverySessionStart,
  isDeliverySessionExpired,
} from './utils/deliverySession';

const TodaysDeliveries = lazy(() => import('./components/TodaysDeliveries'));
const RouteMap = lazy(() => import('./components/RouteMap'));
const CompletedDeliveries = lazy(() => import('./components/CompletedDeliveries'));
// const PausedCustomers = lazy(() => import('./components/PausedCustomers'));

const AppContent: React.FC = () => {
  const [activeTab, setActiveTab] = useState('basket');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [showSideDrawer, setShowSideDrawer] = useState(false);
  const [basketConfirmed, setBasketConfirmed] = useState(false);
  const { isLoading, setLoading } = useLoading();
  const [previousTab, setPreviousTab] = useState('basket');

  const [partnerCheckedIn, setPartnerCheckedIn] = useState<boolean | null>(() =>
    readPartnerCheckedInFromProfileStorage()
  );
  /** Bump after check-in so basket refetches zone orders auto-assigned while preparing. */
  const [ordersRefreshKey, setOrdersRefreshKey] = useState(0);
  const [showCheckInRequired, setShowCheckInRequired] = useState(false);

  // Set document title
  useEffect(() => {
    document.title = 'Delivery Partner Panel';
  }, []);

  const resetAuthUiState = () => {
    setIsLoggedIn(false);
    setShowSideDrawer(false);
    setPartnerCheckedIn(null);
  };

  /** User-initiated logout: blacklist refresh on server, then clear local session */
  const handleLogout = async () => {
    await logoutDeliveryPartner();
    clearSession();
    resetAuthUiState();
  };

  useEffect(() => {
    const onAuthExpired = () => resetAuthUiState();
    window.addEventListener('auth-expired', onAuthExpired);
    return () => window.removeEventListener('auth-expired', onAuthExpired);
  }, []);

  useEffect(() => {
    const storedToken = localStorage.getItem('access') || localStorage.getItem('token');
    const storedFullName = localStorage.getItem('fullName');
    const storedUsername = localStorage.getItem('username');

    if (storedToken) {
      if (getDeliverySessionStart() === null) {
        startDeliverySession();
      }
      if (isDeliverySessionExpired()) {
        clearSession();
        return;
      }
      scheduleAccessTokenRefresh();
      setIsLoggedIn(true);
      if (storedFullName) {
        setUsername(storedFullName);
      } else if (storedUsername) {
        setUsername(storedUsername);
      }
      setPartnerCheckedIn(readPartnerCheckedInFromProfileStorage());
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;
    return subscribeMidnightAttendanceReset(() => {
      setPartnerCheckedIn(false);
    });
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) return;
    const tick = () => {
      if (isDeliverySessionExpired()) {
        clearSession();
      }
    };
    tick();
    const intervalId = window.setInterval(tick, 60_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isLoggedIn]);

  const handleLogin = async (name: string) => {
    // Save username for backward compatibility
    localStorage.setItem('username', name);
    setUsername(name);
    setIsLoggedIn(true);
    
    setLoading(true);
    
    try {
      const profileResponse = await getDeliveryProfile();
      const inner =
        (profileResponse as unknown as { data?: Record<string, unknown> }).data ??
        (profileResponse as unknown as Record<string, unknown>);

      // Map profile API response to the format expected by SideDrawer
      const userObj = inner.user as Record<string, unknown> | undefined;
      const mappedProfile = {
        ...inner,
        employeeName:
          (inner.full_name as string | undefined) ||
          (inner.employeeName as string | undefined) ||
          (userObj?.full_name as string | undefined) ||
          (inner.name as string | undefined) ||
          "Not Available",
        emergencyContact: getEmergencyContactDisplay(inner),
        deliveryManagerPhone: getDeliveryManagerPhone(inner),
        permanentAddress:
          (inner.permanent_address as string | undefined) ||
          (inner.permanentAddress as string | undefined) ||
          (inner.home_address as string | undefined) ||
          (inner.homeAddress as string | undefined) ||
          (inner.address as string | undefined) ||
          "Not Available",
        user: userObj ?? inner,
        partner: inner.partner,
      };
      
      localStorage.setItem("profile", JSON.stringify(mappedProfile));
      setPartnerCheckedIn(readPartnerCheckedInFromProfileStorage());
    } catch (apiError: any) {
      // You can show a toast/notification here if needed
    } finally {
      setLoading(false);
    }
  };

  // Basket confirmation now just tracks last confirm time in MyBasket;
  // user can still pick more orders later, so we no longer lock other tabs here.
  const handleBasketConfirmed = () => {
    if (partnerCheckedIn !== true) return;
    setActiveTab('today');
  };

  // Previously forced user to stay on Basket until first confirmation.
  // Removed so user can freely move between tabs and pick more orders later.

  useEffect(() => {
    // Close side drawer when user logs in
    setShowSideDrawer(false);
  }, [isLoggedIn]);

  // Prevent body scrolling when any overlay/modal is open
  useEffect(() => {
    const isBasketModalOpen = !basketConfirmed && activeTab !== 'basket' && activeTab !== 'today' && activeTab !== 'completed' && activeTab !== 'route';
    const hasOverlayOpen = showSideDrawer || isLoading || isBasketModalOpen;

    if (hasOverlayOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    // Cleanup: restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = '';
    };
  }, [showSideDrawer, isLoading, basketConfirmed, activeTab]);

  if (!isLoggedIn) {
    return <Login onLogin={handleLogin} />;
  }

  const tabSuspenseFallback = <MainContentLoader />;

  return (
    <div className="flex flex-col w-full min-h-screen bg-amber-50/80 relative">
        <header className="bg-orange-500 text-white py-4 px-4 shadow-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img
                src="/Deliveryapplogo.png"
                alt="Genda Phool Logo"
                className="w-10 h-10 rounded-full object-cover mr-3"
              />
              <div>
                <h1 className="text-xl font-bold">Genda Phool</h1>
                <div className="text-sm">Hi, {username}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* {partnerCheckedIn === true && (
                <span className="inline-flex items-center gap-1.5 text-[10px] sm:text-xs font-medium bg-white/20 text-white px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full border border-white/30 max-w-[140px] sm:max-w-none">
                  <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-emerald-300 shrink-0 animate-pulse" aria-hidden />
                  <span className="truncate">Checked in</span>
                </span>
              )}
              {partnerCheckedIn === false && (
                <span className="inline-flex items-center gap-1.5 text-[10px] sm:text-xs font-medium bg-white/15 text-white/95 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full border border-white/25 max-w-[120px] sm:max-w-none">
                  <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-gray-300 shrink-0" aria-hidden />
                  <span className="truncate">Off shift</span>
                </span>
              )} */}
              <NotificationBell
                onOpenTab={(tab) => {
                  setActiveTab(tab);
                }}
              />
              <button
                onClick={() => setShowSideDrawer(true)}
                className="p-2 bg-orange-600 rounded-full hover:bg-orange-600/90"
                aria-label="Open menu"
              >
                <MenuIcon size={20} />
              </button>
            </div>
          </div>
        </header>
        
        {/* Hide search bar on Route tab as requested */}
        {activeTab !== 'route' && <GlobalSearch />}

        <main className="flex-1 overflow-y-auto relative pb-[calc(5.5rem+max(1rem,env(safe-area-inset-bottom)))]">
          <div className="relative min-h-[calc(100dvh-10.5rem)]">
            {isLoading && <MainContentLoader overlay />}
            {activeTab === 'basket' && (
              <MyBasket
                onConfirm={handleBasketConfirmed}
                isConfirmed={basketConfirmed}
                ordersRefreshKey={ordersRefreshKey}
                partnerCheckedIn={partnerCheckedIn}
                onCheckInRequired={() => setShowCheckInRequired(true)}
              />
            )}
            {activeTab === 'today' && (
              <Suspense fallback={tabSuspenseFallback}>
                <TodaysDeliveries />
              </Suspense>
            )}
            {activeTab === 'route' && (
              <Suspense fallback={tabSuspenseFallback}>
                <RouteMap />
              </Suspense>
            )}
            {activeTab === 'completed' && (
              <Suspense fallback={tabSuspenseFallback}>
                <CompletedDeliveries />
              </Suspense>
            )}
            {/* {activeTab === 'paused' && (
              <Suspense fallback={tabSuspenseFallback}>
                <PausedCustomers />
              </Suspense>
            )} */}
          </div>

          {!basketConfirmed && activeTab !== 'basket' && activeTab !== 'today' && activeTab !== 'completed' && activeTab !== 'route' && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              onClick={() => setActiveTab('basket')}
            >
              <div 
                className="bg-white rounded-lg p-6 max-w-sm"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-lg font-medium text-red-600 mb-2">
                  Basket Not Confirmed
                </h3>
                <p className="mb-4">
                  Please confirm your basket items before proceeding to other
                  sections.
                </p>
                <button
                  onClick={() => setActiveTab('basket')}
                  className="w-full py-2 bg-orange-500 text-white rounded-lg font-medium"
                >
                  Go to Basket
                </button>
              </div>
            </div>
          )}
        </main>
          
        <BottomNavBar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          basketConfirmed={basketConfirmed}
        />

        <CheckInRequiredModal
          open={showCheckInRequired}
          onClose={() => setShowCheckInRequired(false)}
          onOpenCheckIn={() => {
            setShowCheckInRequired(false);
            setShowSideDrawer(true);
          }}
        />

        {showSideDrawer && (
          <SideDrawer
            isOpen={showSideDrawer}
            onClose={() => setShowSideDrawer(false)}
            username={username}
            onLogout={handleLogout}
            onPartnerAvailabilityChange={(available) => {
              setPartnerCheckedIn(available);
              if (available === true) {
                setShowCheckInRequired(false);
                setOrdersRefreshKey((k) => k + 1);
              }
            }}
          />
        )}
      </div>
  );
};

export const App: React.FC = () => {
  return (
    <ToastProvider>
      <SearchProvider>
        <LoadingProvider>
          <AppContent />
        </LoadingProvider>
      </SearchProvider>
    </ToastProvider>
  );
};
